import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { acceptInvite } from '../api/staff'
import { ButtonLink } from '../components/ui/Button'
import { useAuth } from '../context/AuthContext'

export default function AcceptInvite() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const { user } = useAuth()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [practiceName, setPracticeName] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid invitation link — no token found.')
      return
    }
    if (!user) {
      // Not logged in — redirect to login with return URL
      void navigate(`/login?next=${encodeURIComponent(`/accept-invite?token=${token}`)}`, { replace: true })
      return
    }

    setStatus('loading')
    acceptInvite(token)
      .then((member) => {
        setPracticeName(`Practice #${member.practice_id}`)
        setStatus('success')
      })
      .catch((err) => {
        setMessage(err instanceof Error ? err.message : 'Failed to accept invitation.')
        setStatus('error')
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const pageClass = 'min-h-screen bg-bg flex items-center justify-center p-8'
  const cardClass = 'bg-surface rounded-xl shadow-card p-10 w-full max-w-[440px] text-center'

  if (status === 'loading' || status === 'idle') {
    return (
      <div className={pageClass}>
        <div className={cardClass}>
          <div className="text-[2rem] mb-4">⏳</div>
          <h2 className="m-0 mb-2 text-text-heading">Accepting invitation…</h2>
          <p className="text-text-3 m-0">Please wait a moment.</p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className={pageClass}>
        <div className={cardClass}>
          <div className="text-[2.5rem] mb-4">✅</div>
          <h2 className="m-0 mb-3 text-text-heading">You're in!</h2>
          <p className="text-text-2 mb-6">
            You've joined {practiceName || 'the practice'} successfully.
          </p>
          <ButtonLink to="/dashboard">Go to Dashboard</ButtonLink>
        </div>
      </div>
    )
  }

  return (
    <div className={pageClass}>
      <div className={cardClass}>
        <div className="mb-4">
          <AlertTriangle size={16} strokeWidth={1.75} className="align-middle" />
        </div>
        <h2 className="m-0 mb-3 text-text-heading">Invitation problem</h2>
        <p className="text-danger-text mb-6">{message}</p>
        <ButtonLink to="/dashboard" variant="outline">Back to Dashboard</ButtonLink>
      </div>
    </div>
  )
}
